import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import GridView from "src/Components/GridView/GridView";
import SmallDialog from "src/Components/Dialog/SmallDialog";
import MoreOptionsMenu from "src/Components/MoreOptions/MoreOptions";
import { InfoMessageType } from "src/Components/InfoBar/InfoBar";
import { IClinicInfo } from "src/Utils/Hooks/GetClinicInfo/useGetClinicInfo";
import { useClinicLogo } from "src/Utils/Hooks/ClinicLogo/useClinicLogo";
import { useClinicContext } from "src/Utils/Hooks/ClinicContext";
import { InfoIcon, UploadIcon } from "src/SharedComponents/Icons/Icons";
import { logger } from "src/Utils/LoggerService/LoggerService";
import './ClinicLogo.css';

interface IClinicLogoParams {
    clinicDetails: IClinicInfo;
    updatePageLoader: (load: boolean) => void;
    setInfo: (message: string, type: InfoMessageType) => void;
}

const MAX_LOGO_FILE_SIZE_BYTES = 5 * 1024 * 1024;

// The API returns the logo as a bare base64 string with no data-URI scheme,
// unlike the blob: URLs used for the local optimistic preview.
const toDisplayableSrc = (logo?: string) => {
    if (!logo) {
        return undefined;
    }
    return /^(data:|blob:|https?:)/.test(logo) ? logo : `data:image/png;base64,${logo}`;
};

const ClinicLogo: React.FC<IClinicLogoParams> = ({ clinicDetails, updatePageLoader, setInfo }) => {
    const { t } = useTranslation();
    const { currentUser } = useClinicContext();
    const {
        uploadClinicLogo, uploadResponse, uploadLoading, uploadError,
        deleteClinicLogo, deleteResponse, deleteLoading, deleteError
    } = useClinicLogo();
    const [logoPreview, setLogoPreview] = useState<string | undefined>(toDisplayableSrc(clinicDetails.logo));
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showMaxSizeError, setShowMaxSizeError] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const localPreviewUrlRef = useRef<string | null>(null);

    const revokeLocalPreview = useCallback(() => {
        if (localPreviewUrlRef.current) {
            URL.revokeObjectURL(localPreviewUrlRef.current);
            localPreviewUrlRef.current = null;
        }
    }, []);

    useEffect(() => {
        revokeLocalPreview();
        setLogoPreview(toDisplayableSrc(clinicDetails.logo));
    }, [clinicDetails.logo, revokeLocalPreview]);

    useEffect(() => revokeLocalPreview, [revokeLocalPreview]);

    useEffect(() => {
        if (uploadResponse) {
            logger.logInformation(`Updated clinic logo successfully, clinicId is ${clinicDetails.clinicId}.`);
            setInfo(t('ClinicLogoUpdatedSuccessMsg'), InfoMessageType.success);
        }
    }, [uploadResponse, setInfo, t, clinicDetails.clinicId]);

    useEffect(() => {
        if (uploadError) {
            logger.logError(`Error occured while updating the clinic logo for clinic id: ${clinicDetails.clinicId}.`);
            setInfo(uploadError, InfoMessageType.error);
            revokeLocalPreview();
            setLogoPreview(toDisplayableSrc(clinicDetails.logo));
        }
    }, [uploadError, setInfo, clinicDetails.clinicId, clinicDetails.logo, revokeLocalPreview]);

    useEffect(() => {
        if (deleteResponse) {
            logger.logInformation(`Deleted clinic logo successfully, clinicId is ${clinicDetails.clinicId}.`);
            setInfo(t('ClinicLogoDeletedSuccessMsg'), InfoMessageType.success);
            setLogoPreview(undefined);
        }
    }, [deleteResponse, setInfo, t, clinicDetails.clinicId]);

    useEffect(() => {
        if (deleteError) {
            logger.logError(`Error occured while deleting the clinic logo for clinic id: ${clinicDetails.clinicId}.`);
            setInfo(deleteError, InfoMessageType.error);
        }
    }, [deleteError, setInfo, clinicDetails.clinicId]);

    useEffect(() => {
        updatePageLoader(uploadLoading || deleteLoading);
    }, [uploadLoading, deleteLoading, updatePageLoader]);

    const onLogoSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) {
            return;
        }
        if (!file.type.startsWith('image/')) {
            setShowMaxSizeError(false);
            setInfo(t('InvalidLogoFileType'), InfoMessageType.error);
            return;
        }
        const isAddingNewLogo = !logoPreview;
        if (isAddingNewLogo && file.size > MAX_LOGO_FILE_SIZE_BYTES) {
            setShowMaxSizeError(true);
            return;
        }
        setShowMaxSizeError(false);
        revokeLocalPreview();
        localPreviewUrlRef.current = URL.createObjectURL(file);
        setLogoPreview(localPreviewUrlRef.current);
        uploadClinicLogo(file, clinicDetails.clinicId);
    }, [uploadClinicLogo, setInfo, t, revokeLocalPreview, clinicDetails.clinicId, logoPreview]);

    const confirmDeleteLogo = useCallback(() => {
        setShowDeleteConfirm(false);
        deleteClinicLogo(clinicDetails.clinicId);
    }, [deleteClinicLogo, clinicDetails.clinicId]);

    return (
        <GridView
            title={t('ClinicLogo')}
            headerNode={
                <div className="col-4 d-flex justify-content-end">
                    <button
                        type="button"
                        className="clinic-logo-info-icon"
                        data-tooltip={t('ClinicLogoInfoTooltip')}
                        data-flow="bottom"
                        data-testid="clinic-logo-info-icon">
                        <InfoIcon />
                    </button>
                </div>
            }>
            {showDeleteConfirm &&
                <SmallDialog
                    submitButtonText={t('DeleteLogo')}
                    title={t('DeletingLogo')}
                    submitButtonColor="var(--color-error)"
                    cancelButtonColor="black"
                    onClose={() => setShowDeleteConfirm(false)}
                    onSubmit={confirmDeleteLogo}>
                    <div className="mt-2" style={{ textAlign: 'center' }}>
                        {t('DeleteLogoInstruction')}
                    </div>
                </SmallDialog>
            }
            <div className="clinic-logo-content">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="clinic-logo-file-input"
                    onChange={onLogoSelected}
                    data-testid="clinic-logo-file-input" />
                {logoPreview ?
                    <div className="clinic-logo-preview-row">
                        <div className="clinic-logo-image-frame">
                            <img
                                src={logoPreview}
                                alt={t('ClinicLogo')}
                                className="clinic-logo-preview"
                                data-testid="clinic-logo-preview" />
                        </div>
                        <MoreOptionsMenu id="three-dots-clinic-logo" disabled={!currentUser?.isClinicAdmin}>
                            <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                <li>
                                    <div
                                        className="dropdown-item"
                                        id="upload-new-logo"
                                        onClick={() => fileInputRef.current?.click()}>
                                        {t('UploadNewLogo')}
                                    </div>
                                </li>
                                <li>
                                    <div
                                        className="dropdown-item"
                                        id="delete-logo"
                                        onClick={() => setShowDeleteConfirm(true)}>
                                        {t('DeleteLogo')}
                                    </div>
                                </li>
                            </ul>
                        </MoreOptionsMenu>
                    </div> :
                    <>
                        <button
                            type="button"
                            className="add-logo-button"
                            disabled={uploadLoading || deleteLoading || !currentUser?.isClinicAdmin}
                            onClick={() => fileInputRef.current?.click()}
                            data-testid="add-logo-button">
                            <UploadIcon />
                            <span>{t('AddLogo')}</span>
                        </button>
                        {showMaxSizeError &&
                            <span className="add-logo-max-size" data-testid="add-logo-max-size-error">
                                {t('MaxLogoFileSize')}
                            </span>
                        }
                    </>
                }
            </div>
        </GridView>
    );
}
export default ClinicLogo;
